import { JwtPayload, RequestGuestAccountInputDto, Roles, TokenResultBody } from '@llx/common';
import { jwtDecode } from 'jwt-decode';
import Keycloak from 'keycloak-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { config } from '@/config';
import { captureSentry } from '@/utils/sentryHelper';

import {
  clearPersistedTokens,
  createKeycloakInstance,
  loadPersistedTokens,
  persistTokens,
  persistTokensRaw,
} from './keycloak';

let globalRefreshPromise: Promise<string | null> | null = null;

type BaseContextType = {
  getFreshToken: () => Promise<string | null>;
  askForGuestAccess: (args: RequestGuestAccountInputDto) => Promise<void>;
  isRequestingGuestAccount: boolean;
  login: (returnUrl?: string) => void;
  logout: () => void;
};

type InvalidTokenContextType = BaseContextType & {
  tokenStatus: 'invalid';
};

type LoadingTokenContextType = BaseContextType & {
  tokenStatus: 'loading';
};

type ValidTokenContextType = BaseContextType & {
  tokenStatus: 'valid';
  isAdmin: boolean;
  userID: string;
  email: string;
};

export type KeycloakAuthContextType =
  | InvalidTokenContextType
  | LoadingTokenContextType
  | ValidTokenContextType;

const KeycloakAuthContext = createContext<KeycloakAuthContextType | undefined>(undefined);

type Props = {
  children: React.ReactNode;
};

export function KeycloakProvider({ children }: Props) {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [isLoggingInAsGuest, setIsLoggingInAsGuest] = useState(false);
  const [decodedToken, setDecodedToken] = useState<JwtPayload | null>(null);

  const keycloakRef = useRef<Keycloak | null>(null);

  const decodeAndStore = useCallback((kc: Keycloak) => {
    if (!kc.token) {
      setDecodedToken(null);
      return;
    }
    try {
      setDecodedToken(jwtDecode<JwtPayload>(kc.token));
    } catch (err) {
      captureSentry({ title: 'Failed to decode keycloak token', error: err });
      setDecodedToken(null);
    }
  }, []);

  const setupEventListeners = useCallback(
    (kc: Keycloak) => {
      kc.onAuthSuccess = () => {
        persistTokens(kc);
        decodeAndStore(kc);
        setAuthenticated(true);
      };

      kc.onAuthRefreshSuccess = () => {
        persistTokens(kc);
        decodeAndStore(kc);
        setAuthenticated(true);
      };

      kc.onTokenExpired = () => {
        kc.updateToken(30).catch(() => {
          clearPersistedTokens();
          setAuthenticated(false);
          setDecodedToken(null);
        });
      };

      kc.onAuthLogout = () => {
        clearPersistedTokens();
        setAuthenticated(false);
        setDecodedToken(null);
      };

      kc.onAuthError = () => {
        clearPersistedTokens();
        setAuthenticated(false);
        setDecodedToken(null);
      };
    },
    [decodeAndStore]
  );

  useEffect(() => {
    const kc = createKeycloakInstance();
    keycloakRef.current = kc;
    setupEventListeners(kc);

    const stored = loadPersistedTokens();
    const hasStoredTokens = !!(stored.token && stored.refreshToken);

    kc.init({
      ...(hasStoredTokens && {
        token: stored.token,
        refreshToken: stored.refreshToken,
        idToken: stored.idToken,
      }),
      pkceMethod: 'S256',
      checkLoginIframe: false,
      scope: 'openid email phone offline_access',
    })
      .then((auth) => {
        if (auth) {
          persistTokens(kc);
          decodeAndStore(kc);
          setAuthenticated(true);
          setInitialized(true);
          return;
        }

        if (hasStoredTokens && stored.refreshToken) {
          kc.refreshToken = stored.refreshToken;
          return kc
            .updateToken(-1)
            .then(() => {
              persistTokens(kc);
              decodeAndStore(kc);
              setAuthenticated(true);
              setInitialized(true);
            })
            .catch(() => {
              clearPersistedTokens();
              setAuthenticated(false);
              setInitialized(true);
            });
        }

        setAuthenticated(false);
        setInitialized(true);
      })
      .catch((err) => {
        captureSentry({ title: 'Keycloak init failed', error: err });
        clearPersistedTokens();
        setAuthenticated(false);
        setInitialized(true);
      });

    return () => {
      kc.onAuthSuccess = undefined;
      kc.onAuthRefreshSuccess = undefined;
      kc.onTokenExpired = undefined;
      kc.onAuthLogout = undefined;
      kc.onAuthError = undefined;
    };
  }, []);

  const getFreshToken = useCallback(async (): Promise<string | null> => {
    const kc = keycloakRef.current;
    if (!kc?.token) return null;

    if (globalRefreshPromise) {
      try {
        return await globalRefreshPromise;
      } catch {
        return null;
      }
    }

    globalRefreshPromise = (async () => {
      try {
        await kc.updateToken(30);
        persistTokens(kc);
        return kc.token ?? null;
      } catch {
        clearPersistedTokens();
        setAuthenticated(false);
        setDecodedToken(null);
        return null;
      } finally {
        globalRefreshPromise = null;
      }
    })();

    return globalRefreshPromise;
  }, []);

  const login = useCallback((returnUrl?: string) => {
    const kc = keycloakRef.current;
    if (!kc) return;

    const redirectUri = returnUrl
      ? `${window.location.origin}${returnUrl}`
      : window.location.origin;

    void kc.login({
      redirectUri,
      scope: 'openid email phone offline_access',
    });
  }, []);

  const logout = useCallback(() => {
    const kc = keycloakRef.current;

    clearPersistedTokens();

    if (kc?.authenticated) {
      void kc.logout({ redirectUri: window.location.origin });
    } else {
      setAuthenticated(false);
      setDecodedToken(null);
      window.location.href = '/';
    }
  }, []);

  const loginAsGuest = useCallback(
    async (args: RequestGuestAccountInputDto): Promise<void> => {
      setIsLoggingInAsGuest(true);
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);

        let response: Response;
        try {
          response = await fetch(`${config.BACKEND_URL}/auth/guest-account`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(args),
            signal: controller.signal,
          });
        } finally {
          clearTimeout(timeoutId);
        }

        if (!response.ok) {
          throw new Error(
            `Guest account request failed: ${response.status} ${response.statusText}`
          );
        }

        const data = (await response.json()) as TokenResultBody;
        const { access_token, refresh_token, id_token } = data;

        persistTokensRaw(access_token, refresh_token, id_token);

        const newKc = createKeycloakInstance();
        setupEventListeners(newKc);
        keycloakRef.current = newKc;

        const auth = await newKc.init({
          token: access_token,
          refreshToken: refresh_token,
          idToken: id_token,
          checkLoginIframe: false,
        });

        if (auth) {
          persistTokens(newKc);
          decodeAndStore(newKc);
          setAuthenticated(true);
        } else {
          clearPersistedTokens();
          setAuthenticated(false);
          throw new Error('Guest login: keycloak-js rejected tokens');
        }
      } catch (err) {
        captureSentry({ title: 'Guest login failed', error: err });
        throw err;
      } finally {
        setIsLoggingInAsGuest(false);
      }
    },
    [setupEventListeners, decodeAndStore]
  );

  const contextValue: KeycloakAuthContextType = useMemo(() => {
    const base: BaseContextType = {
      getFreshToken,
      askForGuestAccess: loginAsGuest,
      isRequestingGuestAccount: isLoggingInAsGuest,
      login,
      logout,
    };

    if (!initialized) {
      return { ...base, tokenStatus: 'loading' } satisfies LoadingTokenContextType;
    }

    if (!authenticated || !decodedToken) {
      return { ...base, tokenStatus: 'invalid' } satisfies InvalidTokenContextType;
    }

    const isAdmin = decodedToken.roles?.includes(Roles.admin) ?? false;
    const userID = decodedToken.sub ?? '';
    const email = decodedToken.email ?? '';

    if (!userID || !email) {
      return { ...base, tokenStatus: 'invalid' } satisfies InvalidTokenContextType;
    }

    return {
      ...base,
      tokenStatus: 'valid',
      isAdmin,
      userID,
      email,
    } satisfies ValidTokenContextType;
  }, [
    initialized,
    authenticated,
    decodedToken,
    getFreshToken,
    loginAsGuest,
    isLoggingInAsGuest,
    login,
    logout,
  ]);

  return (
    <KeycloakAuthContext.Provider value={contextValue}>{children}</KeycloakAuthContext.Provider>
  );
}

export const useKeycloakAuth = () => {
  const context = useContext(KeycloakAuthContext);
  if (context === undefined) {
    throw new Error('useKeycloakAuth must be used within a KeycloakProvider');
  }
  return context;
};

export const useAuthTokenContext = useKeycloakAuth;
